import { HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { IPaginationResponse, IBaseResponse } from '@carm/_utils/models';
import { PaginatedList } from '@carm/_utils/models/pagination-response.model';
import { Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BaseApiService } from '../../baseapi.service';
import { UserStore } from '../store';
import {
  Business,
  IUserRolesResponse,
  Program,
  Role,
  RoleTypes,
  THIRD_PARTY_ROLES,
  User,
  UserRole,
  IProgramResponse,
  IBusinessResponse,
  IStaffMemberResponse,
} from '../models';
import { IBusinessListResponse } from '../models/user-role.model';
import { ProgramFacadeService } from '@carm/_core/program-account-profile/services/program-facade.service';

/**
 * AccessUserService to handle roles related functionality
 */
@Injectable()
export class AccessUserService extends BaseApiService {
  /**
   * Creates an instance of user service.
   * @param injector require for BaseApiService
   */
  constructor(
    protected injector: Injector,
    protected store: Store<UserStore.State>,
    protected programFacadeService: ProgramFacadeService,
  ) {
    super(injector);
  }

  /**
   * Gets the user roles from the backend
   * @returns oberservable of user role
   */
  public getUserRoles(): Observable<{ user: User; role: UserRole }> {
    const url = environment.business.default.baseUrl + '/roles';
    return this.httpAuthGet<IUserRolesResponse>(url).pipe(
      map(data => this.mapUserRoleModel(data)),
      tap(data => {
        this.userService.updateUserRoles(data);
        this.programFacadeService.loadInactivePrograms();
        }
      )
    );
  }

  public getRegisteredBusinessUserRoles(includeOrphans: boolean = false): Observable<{ user: User; role: UserRole }> {
    const url = environment.business.default.baseUrl + '/roles';
    return this.httpAuthGet<IUserRolesResponse>(url).pipe(
      map(data => this.mapUserRoleModel(data)),
      tap(data => this.userService.updateUserRoles(data)),
      filter((roleData) => includeOrphans || roleData?.role !== null &&
        roleData?.role?.lastUsedRole !== null && roleData?.role?.lastUsedRole?.roleType !== RoleTypes.Orphan &&
        roleData?.role?.roles?.length > 0),
      take(1),
    );
  }

  /**
   * Gets the user roles from the backend
   * @returns oberservable of user role
   */
  public getUserRolesById(
    businessId: string,
    employeeId: string
  ): Observable<IUserRolesResponse> {
    const url =
      environment.business.default.baseUrl +
      `/business/${businessId}/roles/employee/${employeeId}`;
    return this.httpAuthGet<IUserRolesResponse>(url);
  }

  /**
   * Set the program as LastUsedProgram
   * @returns oberservable of boolean
   */
  public setLastUsedProgram(
    programId: string
  ): Observable<boolean> {

    const bn9 = programId.substring(0,9);

    const url = environment.baseApiUrls + `/business/${bn9}/program/${programId}/roles/selected`;
    return this.httpAuthPost<IBaseResponse>(url, null).pipe(
      map(_ => true),
      tap(_ => this.userService.selectNewBusinessProgramByUid(bn9, programId))
    );
  }

  /**
   * Gets the user businesses list from the backend
   * @returns oberservable of user role
   */
  public getUserBusinessList(
    page: number,
    pageSize: number,
    filterField: string = '',
    filterValue: string = '',
    sort: string = 'legalName',
    filterToBusinessManagement?: boolean
  ): Observable<IBusinessListResponse> {
    let url = '/roles/business/list';
    if (!!filterToBusinessManagement) {
      url += '?filterToBusinessManagement=true';
    }

    const params = new HttpParams()
      .set('currentPage', page.toString())
      .set('pageSize', pageSize.toString())
      .set('filterField', filterField)
      .set('filterValue', filterValue)
      .set('sort', sort);

    const endpoint = `${environment.business.default.baseUrl}/${url}`;
    return this.httpAuthGet<IBusinessListResponse>(endpoint, params).pipe(
      map(data => {
        data.totalRecords = data.pagination?.totalCount;
        return data;
      })
    );
  }

  /**
   * Sets last used business and return UserRole
   * @param businessId
   * @returns last used business
   */
  public setLastUsedBusiness(
    businessId: string
  ): Observable<{ user: User; role: UserRole }> {
    const url =
      environment.baseApiUrls + `/business/${businessId}/roles/selected`;
    return this.httpAuthPost<IUserRolesResponse>(url, null).pipe(
      map(data => this.mapUserRoleModel(data))
    );
  }


  /**
   * Sets last used business and return UserRole
   * @param businessId
   * @returns last used business
   */
  public setNewSelectedBusinessAndProgram(
    businessId: string,
    programId: string,
  ): Observable<{ user: User; role: UserRole }> {
    const url =
      environment.baseApiUrls + `/roles/business?businessId=${businessId}`;
    return this.httpAuthGet<IUserRolesResponse>(url).pipe(
      map(data => this.mapUserRoleModel(data))
    );
  }

  /**
   * Sets last used business and return UserRole
   * @param businessId
   * @returns last used business
   */
  public getStaffRolesByBusiness(businessId: string): Observable<Role[]> {
    const url =
      environment.business.default.baseUrl +
      `/roles/business?businessId=${businessId}`;
    return this.httpAuthGet<IUserRolesResponse>(url).pipe(
      map(data => this.mapUserRoleModel(data).role.roles)
    );
  }

  /**
   * Set Privacy Term and Condition Accepted
   * @returns oberservable of user role
   */
  public setUserTermConditionAcceptedFlag(): Observable<boolean> {
    const url = environment.business.default.baseUrl + '/staffmember/acceptterms';
    return this.httpAuthPost(url, null)
      .pipe(
        map(data => true),
        tap(x => this.userService.setAcceptTermsAndConditions())
      );
  }

  /**
   * Maps user role models from the response body, also set the currentRole
   * @param res Response body
   * @returns user role models
   */
  mapUserRoleModel = (res: IUserRolesResponse): { user: User; role: UserRole } => {
    const roles: Role[] = [];
    let user: User = null;

    let lastUsedRole: Role;
    let business: Business;

    if (res) {
      if (res.roles) {
        res.roles.forEach(resRole => {
          roles.push(this.mapRoleModel(resRole, res.business));
        });
      }
      if (res.business) {
        business = this.mapBusinessModel(res.business);
      } else {
        business = null;
      }
      if (res.lastUsedProgram && roles.length > 0) {
        lastUsedRole = roles.find(
          c => c.program.uid === res.lastUsedProgram.uid
        );
        if (!lastUsedRole){
          lastUsedRole = roles[0];
        }
      } else if (roles.length > 0) {
        lastUsedRole = roles[0];
      } else {
        lastUsedRole = null;
      }

      if (res.staffMember) {
        user = this.mapUserModel(res.staffMember);
      }

      return { user, role: { business, lastUsedRole, roles } };
    }
    return { user, role: { lastUsedRole: null, roles: [], business: null } };
  }

  mapUserModel(res: IStaffMemberResponse): User {
    const user: User = {
      userId: res.uid,
      preferredLanguage: res.preferredLanguage,
      displayUserId: res.displayUid,
      isDefaultAddressExist: false,
      lastLogout: res.lastLogout,
      isPrivacyTermsConditionsAccepted: res.privacyTermsConditionsUse,
    };

    if (res.defaultAddress && res.preferredLanguage) {
      user.firstName = res.defaultAddress.firstName;
      user.lastName = res.defaultAddress.lastName;
      user.isDefaultAddressExist = true;
    }

    return user;
  }

  mapRoleModel(res: any, nativeBusiness?: IBusinessResponse): Role {
    return {
      roleType: res.code,
      roleName: res.name,
      isNative: this.isNativeProgram(res.code, res.program, nativeBusiness),
      program: this.mapProgramModel(res.program),
    };
  }

  mapProgramModel(res: IProgramResponse): Program {
    return {
      programType: res.type,
      uid: res.uid,
      name: res.operatingName,
      operatingName: res.operatingName,
      companyUid: res.parentBusiness ? res.parentBusiness.uid : undefined,
      companyName: res.parentBusiness
        ? res.parentBusiness.legalName
        : undefined,
      programAreaId: res.programAreaId,
      programSubType: res.programSubType,
    };
  }

  mapBusinessPaginatedResponse(res: {
    pagination: IPaginationResponse;
    results: any;
  }): PaginatedList<Business> {
    if (!(res && res.pagination && res.results)) {
      return null;
    }
    const output: PaginatedList<Business> = {
      hasNext: res.pagination.hasNext,
      hasPrevious: res.pagination.hasPrevious,
      page: res.pagination.page,
      totalCount: res.pagination.totalCount,
      totalPages: res.pagination.totalPages,
      data: [],
    };

    res.results.forEach(business => {
      output.data.push(this.mapBusinessModel(business));
    });

    return output;
  }

  mapBusinessModel(res: any): Business {
    return res
      ? {
          legalName: res.legalName,
          operatingName: res.operatingName,
          uid: res.uid,
        }
      : null;
  }

  isNativeProgram(
    res: RoleTypes,
    program?: IProgramResponse,
    nativeBusiness?: IBusinessResponse
  ) {
    if (program && nativeBusiness && program.parentBusiness) {
      return program.parentBusiness.uid === nativeBusiness.uid;
    } else {
      return !THIRD_PARTY_ROLES.includes(res, 0);
    }
  }

  /**
   * Changes current program by uid
   * @param programUid
   */
  changeCurrentProgramByUid(selectedProgram: {
    businessId: string;
    programId: string;
  }) {
    return this.setLastUsedProgram(selectedProgram.programId);
  }
}
